import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  useTransition,
} from 'react';
import * as React from 'react';
import {
  type Location,
  type NavigateOptions,
  Routes,
  type To,
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
  useLocation as useRouterLocation,
  useNavigationType as useRouterNavigationType,
} from 'react-router';

/**
 * Workaround for React Router v7 + React 19 excessive re-renders.
 *
 * BrowserRouter's internal location context updates ~40 times after a single
 * navigation, causing every useLocation() and useNavigate() consumer to
 * re-render repeatedly.
 *
 * This module uses useSyncExternalStore to completely decouple consumers from
 * React Router's context. A thin LocationSync component bridges the two worlds:
 * it re-renders ~40 times (unavoidable), but only pushes one update to the
 * external store per actual navigation (by comparing location.key).
 */

// ─── External location store ───────────────────────────────────────────
// Fully outside React's context system. Components subscribe via
// useSyncExternalStore, so only real changes trigger consumer re-renders.

type NavigationType = 'POP' | 'PUSH' | 'REPLACE';

let _location: Location = {
  pathname: window.location.pathname,
  search: window.location.search,
  hash: window.location.hash,
  state: window.history.state?.usr ?? null,
  key: window.history.state?.key ?? 'default',
  unstable_mask: undefined,
};

let _navigationType: NavigationType = 'POP';

const _listeners = new Set<() => void>();

function _subscribe(cb: () => void) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

function _getSnapshot(): Location {
  return _location;
}

function _getNavTypeSnapshot(): NavigationType {
  return _navigationType;
}

// ─── LocationSync ──────────────────────────────────────────────────────
// Thin bridge: subscribes to React Router's context (re-renders ~40× per
// nav) but only updates the external store once when the key changes.
// Place as a sibling inside BrowserRouter — it's a leaf (returns null).

export function LocationSync() {
  const routerLocation = useRouterLocation();
  const navigationType = useRouterNavigationType();

  // useLayoutEffect runs synchronously before browser paint, so consumers
  // re-render in the same frame — the user never sees stale UI.
  useLayoutEffect(() => {
    if (_location.key === routerLocation.key) return;
    _location = routerLocation;
    _navigationType = navigationType as NavigationType;
    for (const cb of _listeners) cb();
  });

  return null;
}

// ─── Stable hooks ──────────────────────────────────────────────────────

/* Inside <StableRoutes> the location trails the store by one transition (the
   old page stays up while the next one's chunk loads); everything rendered by
   the routes reads that one, so a page never sees a URL it isn't showing. */
type RoutedLocation = { location: Location; navigationType: NavigationType };
const RoutedLocationContext = createContext<RoutedLocation | null>(null);

/**
 * Drop-in replacement for React Router's useLocation().
 * Only re-renders when the location key actually changes (once per nav).
 */
export function useStableLocation(): Location {
  const routed = useContext(RoutedLocationContext);
  const live = useSyncExternalStore(_subscribe, _getSnapshot, _getSnapshot);
  return routed ? routed.location : live;
}

/**
 * Drop-in replacement for React Router's useNavigationType().
 */
export function useStableNavigationType(): NavigationType {
  const routed = useContext(RoutedLocationContext);
  const live = useSyncExternalStore(
    _subscribe,
    _getNavTypeSnapshot,
    _getNavTypeSnapshot,
  );
  return routed ? routed.navigationType : live;
}

/**
 * Drop-in replacement for React Router's useNavigate().
 * Does NOT subscribe to location context, so it never causes re-renders.
 * Uses UNSAFE_NavigationContext.navigator (the BrowserHistory instance)
 * directly — ref: https://github.com/remix-run/react-router/issues/7634
 */
export function useStableNavigate(): (
  to: To | number,
  options?: NavigateOptions,
) => void {
  const { navigator } = useContext(UNSAFE_NavigationContext) as any;

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigator.go(to);
        return;
      }
      if (options?.replace) {
        navigator.replace(to, options.state);
      } else {
        navigator.push(to, options?.state);
      }
    },
    [navigator],
  );
}

// ─── StableRoutes ──────────────────────────────────────────────────────
// Wraps <Routes> in an overridden UNSAFE_LocationContext so that <Routes>
// and everything it renders subscribe to our stable location instead of
// the raw React Router context that fires ~40× per navigation.

export function StableRoutes({ children }: { children: React.ReactNode }) {
  const location = useSyncExternalStore(_subscribe, _getSnapshot, _getSnapshot);
  const navigationType = useSyncExternalStore(
    _subscribe,
    _getNavTypeSnapshot,
    _getNavTypeSnapshot,
  );
  const [shown, setShown] = useState<RoutedLocation>(() => ({
    location,
    navigationType,
  }));
  const [pending, startTransition] = useTransition();

  // a transition: a route whose chunk isn't loaded yet suspends without
  // replacing the current page with the Suspense fallback
  useLayoutEffect(() => {
    if (shown.location === location) return;
    startTransition(() => setShown({ location, navigationType }));
  }, [location, navigationType]);

  const locationCtx = useMemo(() => shown, [shown]);

  // a pushed navigation to another path opens at the top of the shell's
  // scroller (the old page stays mounted while the next one loads, so its
  // offset would otherwise carry over); back / forward keep theirs
  const shownPath = useRef(shown.location.pathname);
  useLayoutEffect(() => {
    if (shownPath.current === shown.location.pathname) return;
    shownPath.current = shown.location.pathname;
    if (shown.navigationType !== 'POP')
      document.querySelector('[data-route-scroll]')?.scrollTo(0, 0);
  }, [shown]);

  return (
    <RoutedLocationContext.Provider value={locationCtx}>
      <UNSAFE_LocationContext.Provider value={locationCtx as any}>
        {pending && (
          <div className="m-route-pending" role="progressbar" aria-busy />
        )}
        <Routes>{children}</Routes>
      </UNSAFE_LocationContext.Provider>
    </RoutedLocationContext.Provider>
  );
}
